/**
 * The games shelf.
 *
 * Data, like the mode registry on the server: adding a game is one HTML file in
 * `public/games/` and one entry here. Nothing else in the app knows how many
 * there are or what they are called.
 *
 * Each one is a standalone document loaded in an iframe, which is what makes
 * that possible. A game is a render loop with its own key handling; putting it
 * in the React tree would mean sharing a keyboard, a frame budget and a
 * lifecycle with a translator. In a frame it shares nothing, and closing the
 * dialog unmounts it — every timer, listener and handler it owns dies with it,
 * so none of them can be left running behind the page.
 *
 * ── On what is here, and what is not ──────────────────────────────────────
 *
 * These are all written for this repo. Not because embedding somebody else's
 * game is hard, but because the two obvious ways of doing it are both worse:
 * an `<iframe>` pointed at a games site is a third party that can break the
 * page, watch the visitor, or start showing ads on a company's own site, and
 * most of them refuse to be framed at all. A rewritten classic has none of
 * that, works offline, and is a few kB.
 *
 * It is also the honest answer to "add Mario", and to "where is Contra". Both
 * are somebody's property — Nintendo's and Konami's — and the sites that offer
 * them in an iframe are not licensed to. `coinrun` and `commando` are the two
 * GENRES those names are used to mean (run right, collect, stomp, reach the
 * flag; and run right, jump, shoot everything, reach the end), drawn from
 * scratch here. That is the part that was actually being asked for, and the
 * part that is legal to put on a business's home page.
 */

/**
 * Which shelf a game sits on.
 *
 * Thirty-one names in one column is a wall of text nobody reads to the bottom
 * of. Three headings turn it into a choice: something to react to, something to
 * think about, or something to play against an opponent.
 */
export type GameGroup = 'big' | 'arcade' | 'puzzle' | 'board';

export const GROUP_LABEL: Record<GameGroup, string> = {
  big: 'Big games',
  arcade: 'Arcade',
  puzzle: 'Puzzles',
  board: 'Board & cards',
};

export interface Game {
  id: string;
  group: GameGroup;
  /** Shown in the menu and as the dialog's title. */
  name: string;
  /** One line under the name — what you do, not what it is like. */
  blurb: string;
  /** The document to load, under public/. */
  file: string;
  /**
   * The tile drawn beside the name in the menu.
   *
   * Inline SVG rather than image files: six more network requests for six
   * 40px thumbnails is a slower menu than one that is already in the bundle,
   * and these follow the theme because they are drawn in `currentColor`.
   */
  art:
    | 'snake' | 'bricks' | 'grid2048' | 'cards' | 'mines' | 'runner' | 'bird' | 'noughts'
    | 'blocks' | 'paddle' | 'invader' | 'climb' | 'mole' | 'echo' | 'four' | 'gem'
    | 'sudoku' | 'solitaire' | 'maze'
    | 'soldier' | 'car' | 'frog' | 'balloon' | 'fruit' | 'stack' | 'splat'
    | 'bubble' | 'flow' | 'crate' | 'tank' | 'barrage'
    | 'starship' | 'dungeon' | 'turret' | 'sprinter' | 'racecar'
    | 'banner' | 'knight' | 'orbit' | 'gemgrid' | 'notes' | 'tunnel' | 'coin' | 'fighter'
    | 'castle' | 'loaf' | 'sheep' | 'gulp' | 'bucket' | 'bomb' | 'footprints' | 'wand';
  /**
   * The colour of this game's tile in the menu.
   *
   * The first eighteen games all draw in the app's blue accent, which is right
   * for a translator and turns the shelf itself into a column of identical blue
   * squares. The vivid games (see `body.vivid` in _arcade.css) each get their
   * own hue instead, so the menu previews what is behind the name.
   *
   * A fixed set of names rather than free-form colour, because Tailwind only
   * ships the classes it can SEE in the source — a computed class string is
   * silently dropped at build time and the tile renders with no colour at all.
   */
  tint?: 'lime' | 'orange' | 'cyan' | 'pink' | 'rose' | 'amber' | 'violet' | 'sky' | 'emerald' | 'fuchsia'
    | 'indigo' | 'red' | 'teal' | 'purple' | 'yellow' | 'green' | 'blue';
  /**
   * The day this game went on the shelf, ISO `YYYY-MM-DD`.
   *
   * Only there to drive the NEW badge, and the badge expires on its own — see
   * `isNew` below. A hand-set `isNew: true` flag is a lie with a delay on it:
   * it is correct on the day it is written and nobody ever comes back to
   * remove it.
   */
  added?: string;
  /**
   * Off the shelf, without being gone.
   *
   * The game keeps its entry and its HTML file; it simply is not in `GAMES`,
   * so it is not in the menu, not in the count, and not in the previous/next
   * walk either — a hidden game you could still step onto with the arrows
   * would be the worst of both. Put it back by deleting the one line.
   *
   * A flag rather than a deletion because taking the entry out would take the
   * file's only reference with it, and `public/games/<id>.html` would sit
   * there unexplained until somebody deleted it as dead weight.
   */
  hidden?: boolean;
  /**
   * On the shelf only for these accounts (nicknames, case-insensitive); nobody
   * else — signed out included — sees it in the menu, the count or the walk.
   * Read through `gamesFor`, never by filtering `GAMES` by hand.
   */
  onlyFor?: string[];
  /**
   * Escape opens this game's own menu (pause / level select / main menu).
   *
   * The dialog shows an "Esc · Game menu" chip only for these, and clicking it
   * sends a synthetic Escape into the frame. A chip on a game that ignores the
   * key would be a button that does nothing, so it is opt-in per game.
   */
  escMenu?: boolean;
}

/**
 * Has this game been on the shelf for less than a month?
 *
 * Deliberately computed rather than stored, so nothing has to be edited later
 * for the badges to go away. Called from a component that only renders after a
 * click, so there is no server/client rendering to disagree about the date.
 */
const NEW_FOR_DAYS = 30;

/**
 * The games that pay KHARMA for play: the server has a reward table for them
 * (MISSION_GAMES in backend/src/lib/missionRewards.ts) AND the page asks for a
 * ticket when a mission starts and redeems it on a clear. Audited 2026-10-14.
 *
 * Not the same list as MISSION_GAMES: Pocket Barrage and Glowcoil have a table
 * there but never ask for a ticket, and several kit games ask but have no table,
 * so none of those pay. A game joins this set only when both halves are wired.
 * The shelf marks these cards with the KHARMA coin.
 */
export const PAYS_KHARMA: ReadonlySet<string> = new Set([
  'contra', 'brawler', 'jeep', 'swarm', 'castle', 'ironline', '2048',
  'mandate', 'tanks', 'bakery', 'sheepwar', 'bubblewar', 'prank',
  'sudoku', 'breaker', 'emberkeep', 'coinrun', 'depths', 'neonrun', 'nebula', 'turbo', 'prism', 'rivercross',
]);

export function isNew(game: Game, now: number = Date.now()): boolean {
  if (!game.added) return false;
  const added = Date.parse(game.added + 'T00:00:00Z');
  if (Number.isNaN(added)) return false;
  const days = (now - added) / 86_400_000;
  return days >= 0 && days < NEW_FOR_DAYS;
}

/**
 * Every game that exists, including the ones taken off the shelf.
 *
 * Not exported: `GAMES` below is what the app should ask for. Anything that
 * read this list would be opting into the hidden ones by accident.
 */
/** The two crypto games are private to these accounts (owner's request, 2026-10-09). */
const CRYPTO_TEAM = ['grit', 'apollo'];

const ALL_GAMES: Game[] = [
  {
    id: 'abyssal', escMenu: true, group: 'big', name: 'Abyssal',
    blurb: 'Hunt, grow and evolve through ten sharks to Megalodon across five oceans, missions and survival challenges',
    file: '/games/abyssal.html', art: 'gulp', tint: 'cyan', added: '2026-10-14',
  },

  {
    id: 'bounty', escMenu: true, group: 'big', name: 'Bounty',
    blurb: 'Lead Three Kingdoms legends through 18 tactical battles over mountain passes, rivers and raised fortresses',
    file: '/games/bounty.html', art: 'knight', tint: 'emerald', added: '2026-10-13',
  },

  /*
   * Five single-player games added 2026-10-13, from the owner's list (castle
   * defence, bread service, sheep war, bubble battle, tricking neighbours). No
   * online play, by the owner's choice; each has a KHARMA shop and pays KHARMA
   * for a cleared mission. Their briefs are docs/games/<id>.md.
   */
  {
    id: 'castle', escMenu: true, group: 'big', name: 'Arrowhold',
    blurb: 'Stand on the wall and shoot the siege down: twenty sieges, one bow, and everything you hit falls on the army below',
    file: '/games/castle.html', art: 'castle', tint: 'blue', added: '2026-10-13',
  },
  {
    id: 'bakery', escMenu: true, group: 'big', name: 'Rye & Shine',
    blurb: 'Shape it, bake it golden, serve it still warm: run Poppy\'s bakery through twenty busier and busier mornings',
    file: '/games/bakery.html', art: 'loaf', tint: 'amber', added: '2026-10-13',
  },
  {
    id: 'sheepwar', escMenu: true, group: 'big', name: 'Lambast',
    blurb: 'A sheep war: send your flock down five droves, out-weigh the rival flock, and ring the bell to make them follow',
    file: '/games/sheepwar.html', art: 'sheep', tint: 'lime', added: '2026-10-13',
  },
  {
    id: 'bubblewar', escMenu: true, group: 'arcade', name: 'Gulp Cove',
    blurb: 'Bubble battle: swallow what is smaller, flee what is bigger, and bite the giants when the tide turns',
    file: '/games/bubblewar.html', art: 'gulp', tint: 'teal', added: '2026-10-13',
  },
  {
    id: 'prank', escMenu: true, group: 'big', name: 'Mischief Row',
    blurb: 'Sneak into the grump\'s cut-away house and rig harmless tricks that tip him from one into the next',
    file: '/games/prank.html', art: 'bucket', tint: 'rose', added: '2026-10-13',
  },
  {
    id: 'mandate', escMenu: true, group: 'big', name: 'Mandate of the Three Kingdoms',
    blurb: 'Historical single-player strategy: choose your leader, listen to your council, and unite the realm across 20 chapters',
    file: '/games/mandate.html', art: 'banner', tint: 'amber', added: '2026-10-13',
  },
  /*
   * Bomb Squad, the COO's game from teamwork (ai-llm-2030/Kharma_Game_COO,
   * branch game/bombsquad @ 3123327), built on the teamwork skeleton: one
   * file, no network, saves in localStorage. Free until the owner sets a
   * price; no KHARMA shop, mission rewards or online play yet.
   */
  {
    id: 'bombsquad', escMenu: true, group: 'arcade', name: 'Bomb Squad',
    blurb: 'Big blasts, tight deadlines. Clear the cargo and escape through 20 challenging missions',
    file: '/games/bombsquad.html', art: 'bomb', tint: 'amber', added: '2026-10-14',
  },
  /*
   * A whole tactics RPG, delivered as its own folder (games/crest/: ES modules
   * and ~28 MB of art and music) rather than one HTML file. It keeps its saves
   * in localStorage; it has no KHARMA shop, tickets or online play yet.
   * Art is Battle for Wesnoth's (GPL v2+) — see games/crest/CREDITS.md.
   */
  {
    id: 'crest', escMenu: true, group: 'big', name: 'Crest of Embers',
    blurb: 'Tactics RPG: 13 story missions on grid maps, 21 heroes to recruit and promote, a weapon triangle and a boss in every battle',
    file: '/games/crest/index.html', art: 'knight', tint: 'orange', added: '2026-10-13',
  },
  /*
   * A seven-chapter party RPG, delivered as its own folder (games/potter/: ES
   * modules, chapter data, ~40 MB of art and music). Harry Potter names and
   * places are used at the owner's request (2026-10-14); all text, code and
   * story data are original. Art is Battle for Wesnoth's (GPL v2+) and
   * public-domain paintings — see games/potter/assets/CREDITS.md. Saves stay
   * in localStorage; no KHARMA shop, tickets or online play yet.
   */
  {
    id: 'potter', escMenu: true, group: 'big', name: "Harry Potter's Adventure",
    blurb: 'Seven years at Hogwarts as a party RPG: explore painted scenes, make choices, brew potions, and fight in Active Time Battles',
    file: '/games/potter/index.html', art: 'wand', tint: 'indigo', added: '2026-10-14',
  },
  /*
   * The CISO's teamwork game (juliaviana0313, ai-llm-2030/Kharma_Game_CISO),
   * one self-contained file on the shared _arcade skeleton: a stick-figure
   * beat 'em up, a fan tribute to Zhu Zhiqiang's 2002 Flash game. Local best
   * score only — no KHARMA shop, tickets or online play yet; free until priced.
   */
  {
    id: 'xiaoxiao', escMenu: true, group: 'big', name: 'Xiao Xiao No.9',
    blurb: 'An ink stickman brawls across a desk, a restaurant and a villa, with a boss in each',
    file: '/games/xiaoxiao.html', art: 'fighter', tint: 'purple', added: '2026-10-14',
  },
  /*
   * The CPO's game (nicolasmorel910226, teamwork repo Kharma_Game_CPO), one
   * self-contained file with its art inlined. Saves stay in localStorage; no
   * KHARMA shop, tickets, leaderboard or online play, and free until the
   * owner sets a price.
   */
  {
    id: 'beastkingdom', escMenu: true, group: 'big', name: 'Beast Kingdom',
    blurb: 'Fuse wild beasts and elemental dragons to conquer castles across a 60-stage campaign',
    file: '/games/beastkingdom.html', art: 'castle', tint: 'emerald', added: '2026-10-14',
  },
  /*
   * The CBO's teamwork game (ai-llm-2030/Kharma_Game_CBO @ d029f1b), one
   * self-contained file on the shared _arcade skeleton: a side-view stealth
   * puzzle in three houses. Saves stay in localStorage; no KHARMA shop,
   * tickets or online play, and free until the owner sets a price.
   */
  {
    id: 'sneakypranks', escMenu: true, group: 'puzzle', name: 'Sneaky Pranks',
    blurb: "Sneak through Grumbold's house, rig silly pranks, then slip out the door",
    file: '/games/sneakypranks.html', art: 'footprints', tint: 'lime', added: '2026-10-14',
  },
  /*
   * The flagship, above even Pocket Barrage: a one-hit-death arcade
   * run-and-gun on the classic 8-way-aim model, eight missions with 3D base
   * corridors, a vertical falls climb and an alien hive. Kept apart from
   * Rumble Squad, which is the cartoon Metal Slug branch of the genre.
   */
  {
    id: 'contra',
    escMenu: true,
    group: 'big',
    name: 'Iron Talon',
    blurb: 'Alien-war run-and-gun: 20 missions across three acts, 3D corridors, KHARMA rewards and a partner bot',
    file: '/games/contra.html',
    art: 'soldier',
    tint: 'red',
    added: '2026-10-06',
  },
  /*
   * Iron Talon's sister on wheels: a top-down armed jeep in the mould of the
   * 1980s jeep shooters (machine gun that fires where you point, a launcher
   * that lobs over walls, POWs to rescue and fly out). Same KHARMA shop, lives,
   * partner bot and mission rewards as Iron Talon (server items `jeep.*`).
   */
  {
    id: 'jeep',
    escMenu: true,
    group: 'big',
    name: 'Desert Fang',
    blurb: 'Armed-jeep war run: 12 missions, rescue POWs, lob shells over walls, KHARMA rewards and a partner jeep',
    file: '/games/jeep.html',
    art: 'car',
    tint: 'amber',
    added: '2026-10-10',
  },
  /*
   * The martial-arts story on the same frame: a belt-scroll brawler (combos,
   * grabs and throws, parries, a rage special) told across 12 missions from a
   * night alley to a mountain temple. Same KHARMA shop, lives, partner fighter
   * and mission rewards as Iron Talon (server items `brawler.*`).
   */
  {
    id: 'brawler',
    escMenu: true,
    group: 'big',
    name: 'Jade Fist',
    blurb: 'Martial-arts story brawler: 12 missions street to temple, combos, throws and parries, KHARMA rewards and a partner fighter',
    file: '/games/brawler.html',
    art: 'fighter',
    tint: 'rose',
    added: '2026-10-10',
  },
  /*
   * The brick-wall tank war, promoted from the arcade row on 2026-10-13: a
   * besieged brickworks city where the wall is the game and your own shells
   * break it too. 20 missions on the same frame as Iron Talon — KHARMA shop,
   * reserve tanks, a partner crew in four tiers, mission rewards (server items
   * `tanks.*`) — plus online co-op and a Core-against-Core duel.
   */
  {
    id: 'tanks',
    escMenu: true,
    group: 'big',
    name: 'Brickfront',
    blurb: 'Brick-wall tank siege: 20 missions guarding the Core, KHARMA rewards, a partner crew, online co-op and duel',
    file: '/games/tanks.html',
    art: 'tank',
    tint: 'orange',
    added: '2026-10-13',
  },
  /*
   * Swarm's loop in work clothes, for a blockchain team: native coins are the
   * heroes, scam tokens and exploits are the swarm, smart-contract defences
   * are the weapons. Spec: docs/SWARM_CRYPTO_PROMPT.md.
   */
  {
    id: 'swarmcrypto',
    onlyFor: CRYPTO_TEAM,
    escMenu: true,
    group: 'big',
    name: 'Swarm·Crypto',
    blurb: 'Pick one of 24 real coins, grow rich and fight the other 23 with trading strategies, disguised as VS Code',
    file: '/games/swarmcrypto.html',
    art: 'coin',
    tint: 'orange',
    added: '2026-10-07',
  },
  /*
   * Tower defence in Swarm·Crypto's world: Iron Line's engine, the routes
   * drawn as git-graph lanes over a VS Code editor, the 24 official coins as
   * the enemy, your own coin as the hero, and towers that are trading
   * strategies (market desk, liquidity pools, launchpads, validator nodes).
   * Spec: docs/IRONLINE_CRYPTO_PROMPT.md.
   */
  {
    id: 'ironlinecrypto',
    onlyFor: CRYPTO_TEAM,
    escMenu: true,
    group: 'big',
    name: 'Iron Line·Crypto',
    blurb: 'Defend your vault on a git graph: coins march in, your strategies rug, arb and sandwich them',
    file: '/games/ironlinecrypto.html',
    art: 'coin',
    tint: 'teal',
    added: '2026-10-08',
  },
  /*
   * The one real fighting game on the shelf: a 3D three-button fighter on the
   * Virtua Fighter 5 model (systems only: every fighter and stage is our own).
   * Sidesteps and circular attacks, throw escapes, counters, crumples, ukemi,
   * walls, breakable fences and ring-outs; 19 fighters on real martial arts
   * plus the ALLOY boss, 12 stages, arcade, ranked ladder, training and a
   * tutorial dojo. Spec: docs/RING_OUT_REVO_PROMPT.md. The earlier 2D cabinet
   * (vf2.html) is kept beside it but no longer on the shelf.
   */
  {
    id: 'vf2',
    escMenu: true,
    group: 'big',
    name: 'Ring Out',
    blurb: '3D arcade fighter: 20 fighters, 12 stages, sidesteps, throw escapes, walls and ring-outs',
    file: '/games/ringout.html',
    art: 'fighter',
    tint: 'cyan',
    added: '2026-10-07',
  },
  /*
   * ── The big ones ────────────────────────────────────────────────────────
   *
   * Their own shelf, because they are not the same KIND of thing as the rest:
   * each is a real engine rather than a rule set drawn on a grid — a parallax
   * starfield with a boss on it, a raycast first-person maze, a tower defence
   * with an economy, a runner with a generated city, a car with actual grip, a
   * platformer with a level somebody drew, a survival arena with a build, a
   * match-three with cascades, a rhythm game that synthesises its own track,
   * and a tunnel with perspective. Twenty-odd kB each, and they earn it.
   *
   * They are listed first and separately for the same reason a shelf has a
   * front: somebody opening this menu for the first time should meet these,
   * not a tic-tac-toe board.
   */
  /*
   * First on the shelf, and it earns the place twice over.
   *
   * It is the only game here with a HAND to play rather than a single move —
   * twenty weapons dealt at random from a pool of thirty-eight, three shots
   * from each, no wind. A duel with one shell is a game about a number; a duel with a
   * dirt mover, a roller and a heat seeker in it is a game about which problem
   * you would rather have.
   *
   * And it is the only one here that two people can play at once. Not split
   * screen — both tanks share one hill and one view of it, because in this
   * genre the shared board IS the game, and half the skill is reading the
   * crater the other player just made.
   *
   * It replaced Tank Duel, which sat here and was the same genre done with a
   * shared inventory and wind. Two artillery games at the front of the shelf
   * was one more than the shelf could justify.
   */
  {
    id: 'barrage',
    escMenu: true,
    group: 'big',
    name: 'Pocket Barrage',
    blurb: 'Thirty-eight weapons, twenty dealt to each side — alone, two of you, or two of you and an intern',
    file: '/games/barrage.html',
    art: 'barrage',
    tint: 'amber',
    added: '2026-09-09',
  },
  {
    id: 'emberkeep',
    escMenu: true,
    group: 'big',
    name: 'Ember Keep',
    blurb: 'A flame-knight\'s campaign: five worlds, bosses, relics and time medals',
    file: '/games/emberkeep.html',
    art: 'knight',
    tint: 'orange',
    added: '2026-08-12',
  },
  {
    id: 'swarm',
    escMenu: true,
    group: 'big',
    name: 'Swarm',
    blurb: 'Survive the night: weapons fire themselves, you build and evolve them',
    file: '/games/swarm.html',
    art: 'orbit',
    tint: 'green',
    added: '2026-08-12',
  },
  {
    id: 'prism',
    escMenu: true,
    group: 'big',
    name: 'Prism Kingdom',
    blurb: '72-level crystal saga: lances, starbursts, prisms and six bosses',
    file: '/games/prism.html',
    art: 'gemgrid',
    tint: 'blue',
    added: '2026-08-12',
  },
  {
    id: 'pulse',
    escMenu: true,
    group: 'big',
    name: 'Riff Rivals',
    blurb: 'Five rivals, 20 sing-off duels. Hear the call, answer it note for note, and push the health bar their way',
    file: '/games/pulse.html',
    art: 'notes',
    tint: 'rose',
    added: '2026-08-12',
  },
  {
    id: 'vortex',
    escMenu: true,
    group: 'big',
    name: 'Vortex',
    blurb: 'Race 30 neon tunnels to the beat, dodge, phase and crack 5 boss gates',
    file: '/games/vortex.html',
    art: 'tunnel',
    tint: 'fuchsia',
    added: '2026-08-12',
  },
  {
    id: 'nebula',
    escMenu: true,
    group: 'big',
    name: 'Nebula Strike',
    blurb: 'Four star systems, multi-phase bosses, medals and a hangar to upgrade',
    file: '/games/nebula.html',
    art: 'starship',
    tint: 'indigo',
    added: '2026-08-12',
  },
  {
    id: 'depths',
    escMenu: true,
    group: 'big',
    name: 'Ashen Depths',
    blurb: 'Delve five cursed deeps: loot, spells, bosses and a dungeon new every time',
    file: '/games/depths.html',
    art: 'dungeon',
    tint: 'red',
    added: '2026-08-12',
  },
  {
    id: 'ironline',
    escMenu: true,
    group: 'big',
    name: 'Iron Line',
    blurb: 'Storybook tower defence: 100 missions across six islands, 12 heroes, specialist towers and a brutal final siege',
    file: '/games/ironline.html',
    art: 'turret',
    tint: 'teal',
    added: '2026-08-12',
  },
  {
    id: 'neonrun',
    escMenu: true,
    group: 'big',
    name: 'Neon Courier',
    blurb: 'Cyber-courier parkour runner: 5 districts, 30 levels, bosses, endless & daily',
    file: '/games/neonrun.html',
    art: 'sprinter',
    tint: 'purple',
    added: '2026-08-12',
  },
  {
    id: 'drift',
    escMenu: true,
    group: 'big',
    name: 'Drift Legends',
    blurb: 'Five cups, 25 events: drift combos, soup runs, boss duels, a garage to tune',
    file: '/games/drift.html',
    art: 'racecar',
    tint: 'yellow',
    added: '2026-08-12',
  },

  /*
   * The vivid ones come first, and they are first on purpose: they are the ones
   * that look like an arcade rather than like the rest of the page, and a shelf
   * is read from the top.
   */
  {
    id: 'commando',
    escMenu: true,
    group: 'big',
    name: 'Rumble Squad',
    blurb: 'Run-and-gun commando war: 15 missions, tanks, POWs, bosses, 2P co-op',
    file: '/games/commando.html',
    art: 'soldier',
    tint: 'lime',
  },
  {
    id: 'turbo',
    escMenu: true,
    group: 'arcade',
    name: 'Five Horizons',
    blurb: 'Sunny coast racing against the clock: 15 stages, a fork after each, 5 endings, missions & 6 cars',
    file: '/games/turbo.html',
    art: 'car',
    tint: 'orange',
  },
  {
    id: 'rivercross',
    escMenu: true,
    group: 'arcade',
    name: 'Hopscotch Highway',
    blurb: 'Hop the endless highway: roads, rivers, rails and a hawk that hates waiting. Four lands, 32 stages, 25 hoppers',
    file: '/games/rivercross.html',
    art: 'frog',
    tint: 'emerald',
  },
  {
    id: 'balloons',
    escMenu: true,
    group: 'big',
    name: 'Balloon Pop',
    blurb: 'Harpoon your way round the world: 18 stages, power-ups, co-op for two',
    file: '/games/balloons.html',
    art: 'balloon',
    tint: 'pink',
  },
  {
    id: 'fruit',
    escMenu: true,
    group: 'big',
    name: 'Blossom Blade',
    blurb: 'Slice fruit on a 36-stage dojo path. Beat the bosses. Never the bomb',
    file: '/games/fruit.html',
    art: 'fruit',
    tint: 'rose',
  },
  {
    id: 'stack',
    escMenu: true,
    group: 'arcade',
    name: 'Loftlight',
    blurb: 'Four cities, 24 towers, one tap. Slice the overhang, chain perfects, fill every window',
    file: '/games/stack.html',
    art: 'stack',
    tint: 'amber',
  },
  {
    id: 'splat',
    escMenu: true,
    group: 'arcade',
    name: 'Turf Tide',
    blurb: 'Five ink isles, 30 stages, bosses at double speed. Loop to claim, cut trails to splat them',
    file: '/games/splat.html',
    art: 'splat',
    tint: 'fuchsia',
  },
  {
    id: 'bubble',
    escMenu: true,
    group: 'puzzle',
    name: 'Lantern Pop',
    blurb: 'Five lantern-lit lands, 40 stages, bosses that throw stones into your wall. Pop three, drop the rest',
    file: '/games/bubble.html',
    art: 'bubble',
    tint: 'cyan',
  },
  {
    id: 'flow',
    escMenu: true,
    group: 'puzzle',
    name: 'Lumen Lines',
    blurb: 'Nine lit districts, 270 one-answer puzzles. Bridges, hexes, warps and shapes, a showpiece closing each',
    file: '/games/flow.html',
    art: 'flow',
    tint: 'violet',
  },
  {
    id: 'crates',
    escMenu: true,
    group: 'puzzle',
    name: 'Rivet & Crate',
    blurb: 'Five warehouses, 50 stages, a foreman drone with opinions. Push, never pull',
    file: '/games/crates.html',
    art: 'crate',
    tint: 'sky',
  },
  {
    id: 'coinrun',
    escMenu: true,
    group: 'arcade',
    name: 'Tumbleberry',
    blurb: 'Four worlds, 16 levels, a castle boss at the end of each. Bop the blocks, grab the Muffin, find all three big coins',
    file: '/games/coinrun.html',
    art: 'runner',
    tint: 'sky',
  },
  {
    id: 'snake',
    escMenu: true,
    group: 'arcade',
    name: 'Glowcoil',
    blurb: 'Steer a glowing serpent through 30 arena stages, cut rivals off and fell 5 Titans',
    file: '/games/snake.html',
    art: 'snake',
  },
  {
    id: 'breaker',
    escMenu: true,
    group: 'arcade',
    name: 'Comet Breaker',
    blurb: 'Four glass worlds, 32 stages, bosses that shoot back. Catch the capsules, dodge the grey one',
    file: '/games/breaker.html',
    art: 'bricks',
  },
  {
    id: '2048',
    escMenu: true,
    group: 'puzzle',
    name: 'Tilefold',
    blurb: 'Slide, fold, double: a 30-stage paper voyage of rocks, ice and gold squares that pays KHARMA, plus Trio and a daily board',
    file: '/games/2048.html',
    art: 'grid2048',
  },
  {
    id: 'hop',
    escMenu: true,
    group: 'arcade',
    name: 'Gustwing',
    blurb: 'One button, four skies, 24 courses, storms that throw lightning. Thread the spires, break the storm',
    file: '/games/hop.html',
    art: 'bird',
  },
  {
    id: 'minesweeper',
    escMenu: true,
    group: 'puzzle',
    name: 'Relic Sweeper',
    blurb: 'Minesweeper as an expedition: five chapters of ruins, hex vaults, fog',
    file: '/games/minesweeper.html',
    art: 'mines',
  },
  {
    id: 'memory',
    escMenu: true,
    group: 'puzzle',
    name: 'Pocket Album',
    blurb: 'Five albums, 30 pages, rivals who flip back. Peek, bomb and swirl cards, ice, drift and triples',
    file: '/games/memory.html',
    art: 'cards',
  },
  {
    id: 'tictactoe',
    escMenu: true,
    group: 'board',
    name: 'Neon Noughts',
    blurb: 'Twenty neon rivals, nine-board Ultimate, fading marks and gravity. Out-think the Gridmaster',
    file: '/games/tictactoe.html',
    art: 'noughts',
  },
  {
    id: 'blockfall',
    escMenu: true,
    group: 'arcade',
    name: 'Tessera Drift',
    blurb: 'Clear rows across five lit worlds and 30 stages. Twist the T, beat the guardians',
    file: '/games/blockfall.html',
    art: 'blocks',
  },
  {
    id: 'maze',
    escMenu: true,
    group: 'arcade',
    name: 'Neon Gobble',
    blurb: 'Five neon mazes, 30 stages, giant jellies that split. Wake the sleepers, then gobble the whole train',
    file: '/games/maze.html',
    art: 'maze',
  },
  {
    id: 'defender',
    escMenu: true,
    group: 'arcade',
    name: 'Starhive',
    blurb: 'Four sectors, 36 stages, a tractor beam that steals your ship. Win it back mid-dive and fly double',
    file: '/games/defender.html',
    art: 'invader',
  },
  {
    id: 'skyclimb',
    escMenu: true,
    group: 'arcade',
    name: 'Scribble Summit',
    blurb: 'Four sketchbook pages, 24 climbs, bosses at the top. Stomp, shoot, never fall off',
    file: '/games/skyclimb.html',
    art: 'climb',
  },
  {
    id: 'moles',
    escMenu: true,
    group: 'arcade',
    name: 'Bonk Burrow',
    blurb: 'Five gardens, 30 stages, Kings that fight back. Bonk the moles, never the bombs',
    file: '/games/moles.html',
    art: 'mole',
  },
  {
    id: 'gems',
    escMenu: true,
    group: 'puzzle',
    name: 'Gemsworn',
    blurb: 'Five realms, 35 duels, one shared board. Match skulls to strike, gems to charge spells',
    file: '/games/gems.html',
    art: 'gem',
  },
  {
    id: 'sudoku',
    escMenu: true,
    group: 'puzzle',
    name: 'Garden of Nine',
    blurb: 'A zen sudoku journey — every solve grows your garden',
    file: '/games/sudoku.html',
    art: 'sudoku',
  },
  {
    id: 'echo',
    escMenu: true,
    group: 'puzzle',
    name: 'Lanternsong',
    blurb: 'Five lit worlds, 30 public-domain songs. Repeat the melody note by note, or tap it as tiles',
    file: '/games/echo.html',
    art: 'echo',
  },
  {
    id: 'solitaire',
    escMenu: true,
    group: 'board',
    name: 'Velvet Parlour',
    blurb: 'Five solitaires, one velvet table: a 30-deal Star Club, five daily deals, and card backs to win',
    file: '/games/solitaire.html',
    art: 'solitaire',
  },
  {
    id: 'fourinarow',
    escMenu: true,
    group: 'board',
    name: 'Fourfall',
    blurb: 'Twenty rivals, four leagues, champions who bend the rules. Drop, pop, blast — line up four',
    file: '/games/fourinarow.html',
    art: 'four',
  },
  {
    id: 'paddle',
    escMenu: true,
    group: 'board',
    name: 'Spin Circuit',
    blurb: 'Table tennis and air hockey across five venues. Curve it, smash it, beat every boss',
    file: '/games/paddle.html',
    art: 'paddle',
  },
];

/**
 * The shelf: every game that is not hidden.
 *
 * Filtered once, here, rather than at each of the places that draw a list.
 * The menu, the search, the group headings, the "N games" count and the
 * previous/next walk all read this one array, so a game leaves the shelf
 * everywhere at once and none of them can be forgotten.
 */
export const GAMES: Game[] = ALL_GAMES.filter((g) => !g.hidden);

/**
 * The shelf as one account sees it: `GAMES` minus the private games this
 * nickname is not on the list for. Pass null for a signed-out browser.
 */
export function gamesFor(nickname: string | null | undefined): Game[] {
  const me = (nickname ?? '').trim().toLowerCase();
  return GAMES.filter((g) => !g.onlyFor || (!!me && g.onlyFor.includes(me)));
}

// ── Console overrides ────────────────────────────────────────────────────────
//
// The admin console can override a game's CATALOGUE fields without a deploy
// (see backend GameConfig + the Games panel). The id, art and file stay fixed
// in the registry above — only the mutable fields below are overridable, and an
// absent field means "use the value written here".

/** The shelves and tints, as plain arrays, for the console's own selects. */
export const GAME_GROUPS: GameGroup[] = ['big', 'arcade', 'puzzle', 'board'];
export const GAME_TINTS: NonNullable<Game['tint']>[] = [
  'lime', 'orange', 'cyan', 'pink', 'rose', 'amber', 'violet', 'sky', 'emerald',
  'fuchsia', 'indigo', 'red', 'teal', 'purple', 'yellow', 'green', 'blue',
];

/** The fields a console override may carry. Absent = keep the registry value. */
export interface GameOverride {
  hidden?: boolean;
  name?: string;
  blurb?: string;
  group?: GameGroup;
  tint?: Game['tint'];
  added?: string;
  onlyFor?: string[];
  order?: number;
}

export type GameOverrides = Record<string, GameOverride>;

/**
 * Every game including the hidden ones — for the CONSOLE only.
 *
 * `GAMES` deliberately drops the hidden entries so nothing on the public side
 * opts into them by accident; the catalogue editor is the one place that must
 * see them all (hiding a game is the thing it edits). Named loudly so a public
 * import of it reads as the mistake it would be.
 */
export function adminGameCatalogue(): Game[] {
  return ALL_GAMES;
}

/** A game with its override applied — only the mutable fields change. */
export function mergeGameOverride(g: Game, o: GameOverride | undefined): Game {
  if (!o) return g;
  return {
    ...g,
    ...(typeof o.hidden === 'boolean' ? { hidden: o.hidden } : {}),
    ...(o.name ? { name: o.name } : {}),
    ...(typeof o.blurb === 'string' ? { blurb: o.blurb } : {}),
    ...(o.group ? { group: o.group } : {}),
    ...(o.tint ? { tint: o.tint } : {}),
    ...(o.added ? { added: o.added } : {}),
    ...(Array.isArray(o.onlyFor) ? { onlyFor: o.onlyFor } : {}),
  };
}

/**
 * The shelf one account sees, WITH console overrides applied.
 *
 * Starts from the full list (so an override can un-hide as well as hide), merges
 * each game's override, drops the hidden ones, keeps only games this nickname
 * may see, and orders by the override's `order` where set (lower first) falling
 * back to the registry sequence — so an unset game keeps its place.
 */
export function gamesForWith(
  nickname: string | null | undefined,
  overrides: GameOverrides | null | undefined
): Game[] {
  const me = (nickname ?? '').trim().toLowerCase();
  const ov = overrides || {};
  return ALL_GAMES.map((g, i) => ({ g: mergeGameOverride(g, ov[g.id]), i, order: ov[g.id]?.order }))
    .filter(({ g }) => !g.hidden)
    .filter(({ g }) => !g.onlyFor || (!!me && g.onlyFor.includes(me)))
    .sort((a, b) => {
      const ao = a.order ?? a.i + 1000; // unset games keep registry order, after the pinned ones
      const bo = b.order ?? b.i + 1000;
      return ao - bo || a.i - b.i;
    })
    .map(({ g }) => g);
}
